import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuthHttp } from '@hooks';
import { useAuth } from '@context';
import dayjs from 'dayjs';
import { Button } from '../../../tailwindComponents/Button';
import { MATCH_LIST } from '../../../constants';
import { notificationsAtom } from '../../../store/jotai';

const NEGOTIATING_MATCH_STATUS = 'Negotiating';
const MAX_PROPOSAL_CARDS = 6;
const CUSTOMER_SHORTLIST_ROUTE = '/customers';

type PendingProposal = {
  id: string;
  warehouseId: string;
  warehouseName?: string;
  userBusinessName?: string;
  desiredStartDate?: string;
  modifiedAt?: string;
  isActionRequiredByLsp?: boolean;
};

const dismissedProposalsStorageKeyFor = (userId?: string) =>
  `dismissedProposalAlerts:${userId ?? 'unknown'}`;

const dismissalKeyFor = (proposal: PendingProposal) =>
  `${proposal.id}:${proposal.modifiedAt ?? ''}`;

const readDismissedProposals = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const proposalArrivedAt = (proposal: PendingProposal) => {
  const arrivedAt = dayjs(proposal.modifiedAt).valueOf();
  return proposal.modifiedAt && !Number.isNaN(arrivedAt) ? arrivedAt : 0;
};

const newestProposalFirst = (a: PendingProposal, b: PendingProposal) =>
  proposalArrivedAt(b) - proposalArrivedAt(a);

const formatStartDate = (value?: string) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : '—';
};

export function PendingProposalsAlert() {
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const { user } = useAuth();
  const notifications = useAtomValue(notificationsAtom);

  const storageKey = dismissedProposalsStorageKeyFor(user?.id);

  const [proposals, setProposals] = useState<PendingProposal[]>([]);
  const [hasFailedToLoadProposals, setHasFailedToLoadProposals] =
    useState(false);
  const [dismissedKeys, setDismissedKeys] = useState<string[]>(() =>
    readDismissedProposals(storageKey)
  );

  const loadPendingProposals = async () => {
    try {
      /*
       * No pagination is sent because Match/List cannot honour it:
       * WarehouseMatchCache hardcodes Size = 500 and ignores
       * pagination.itemsPerPage, so the whole Negotiating set comes back and
       * the trim to MAX_PROPOSAL_CARDS has to happen on this side.
       */
      const { data } = await authHttp.post(
        MATCH_LIST,
        { matchStatuses: [NEGOTIATING_MATCH_STATUS] },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setProposals(
        (data?.matches ?? [])
          .map((row: { match: PendingProposal }) => row.match)
          .filter((match: PendingProposal) => match?.isActionRequiredByLsp)
      );
      setHasFailedToLoadProposals(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setHasFailedToLoadProposals(true);
    }
  };

  useEffect(() => {
    setDismissedKeys(readDismissedProposals(storageKey));
  }, [storageKey]);

  useEffect(() => {
    loadPendingProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const notification = notifications?.[0];
    if (notification?.notificationType?.startsWith('WarehouseMatch.')) {
      loadPendingProposals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const dismissProposal = (proposal: PendingProposal) => {
    const stillPendingKeys = new Set(proposals.map(dismissalKeyFor));
    const remainingDismissals = readDismissedProposals(storageKey).filter(
      (key) => stillPendingKeys.has(key)
    );
    const updatedDismissals = [
      ...remainingDismissals,
      dismissalKeyFor(proposal),
    ];

    localStorage.setItem(storageKey, JSON.stringify(updatedDismissals));
    setDismissedKeys(updatedDismissals);
  };

  const visibleProposals = proposals
    .filter((proposal) => !dismissedKeys.includes(dismissalKeyFor(proposal)))
    .sort(newestProposalFirst);

  const proposalsOnShow = visibleProposals.slice(0, MAX_PROPOSAL_CARDS);
  const overflowProposalCount =
    visibleProposals.length - proposalsOnShow.length;

  if (visibleProposals.length === 0 && !hasFailedToLoadProposals) return null;

  const headline =
    visibleProposals.length === 0
      ? 'Could not check for pending proposals'
      : 'Proposals awaiting your review';

  return (
    <section
      aria-labelledby='pending-proposals-heading'
      aria-live='polite'
      className='bg-amber-50 border-b border-amber-200 px-6 py-4'
    >
      <div className='flex items-center gap-2'>
        <ExclamationTriangleIcon
          aria-hidden='true'
          className='h-5 w-5 text-amber-500'
        />
        <h2
          id='pending-proposals-heading'
          className='text-sm font-semibold text-gray-900'
        >
          {headline}
        </h2>
        {visibleProposals.length > 0 && (
          <span
            data-testid='pending-proposals-count'
            className='inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-600/20'
          >
            {visibleProposals.length}
          </span>
        )}
      </div>

      {hasFailedToLoadProposals && (
        <p className='mt-2 text-xs text-amber-800'>
          Refresh the page to try again.
        </p>
      )}

      {proposalsOnShow.length > 0 && (
        <div className='mt-3 flex flex-wrap gap-3'>
          {proposalsOnShow.map((proposal) => (
            <div
              key={proposal.id}
              className='relative w-full max-w-xs rounded-lg border border-amber-200 border-l-4 border-l-amber-400 bg-white p-3 shadow-sm'
            >
              <button
                type='button'
                aria-label={`Dismiss proposal from ${
                  proposal.userBusinessName || 'shipper'
                }`}
                title='Dismiss'
                onClick={() => dismissProposal(proposal)}
                className='absolute right-2 top-2 rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
              >
                <XMarkIcon aria-hidden='true' className='h-4 w-4' />
              </button>

              <p className='pr-6 text-sm font-semibold text-gray-900 truncate'>
                {proposal.userBusinessName || 'Shipper'}
              </p>
              <p className='mt-0.5 text-xs text-gray-500 truncate'>
                {proposal.warehouseName || '—'}
              </p>

              <div className='mt-2 flex justify-between gap-2 border-t border-gray-100 pt-2 text-xs'>
                <span className='text-gray-500'>Start date:</span>
                <span className='text-gray-900'>
                  {formatStartDate(proposal.desiredStartDate)}
                </span>
              </div>

              <Button
                label='Review proposal'
                intent='primary'
                size='xs'
                className='mt-3 h-auto w-full whitespace-nowrap'
                onClick={() =>
                  navigate(
                    `/warehouse/${proposal.warehouseId}/manager-service-rates?matchId=${proposal.id}`
                  )
                }
              />
            </div>
          ))}

          {overflowProposalCount > 0 && (
            <div className='flex w-full max-w-xs items-center justify-center rounded-lg border border-dashed border-amber-300 bg-white/60 p-3'>
              <Button
                label={`View ${overflowProposalCount} more`}
                intent='tertiary'
                size='xs'
                className='h-auto whitespace-nowrap'
                onClick={() => navigate(CUSTOMER_SHORTLIST_ROUTE)}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
